import { createContext, useContext, ReactNode } from 'react';
import { useDashboardQuery } from '@/hooks/queries/useDashboardQuery';
import type { DashboardStats } from '@/types/dashboard';

interface DashboardContextType {
  /** Aggregated dashboard statistics from the API */
  stats: DashboardStats | undefined;
  /** True while the dashboard stats are loading */
  isLoading: boolean;
  /** Error object if the request failed */
  error: Error | null;
  /** Re-fetch the dashboard stats */
  refresh: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const { data: stats, isLoading, error, refetch } = useDashboardQuery();

  return (
    <DashboardContext.Provider
      value={{
        stats,
        isLoading,
        error: error as Error | null,
        refresh: refetch,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
